import { updateViewImageListByID } from "actions/view-images-action";
import {
  reconcileBulkUploadSnapshot,
  requestBulkUploadSnapshot,
} from "./bulkUploadSnapshot";
import { getBulkRowGuid, getBulkRowTuples } from "./bulkUploadProjection";
import { persistViewImagesStatusProjection } from "./viewImagesPersistence";
import {
  getViewImagesRetryableRows,
  getViewImagesRowTuples,
  isViewImagesKnownStatus,
} from "./viewImagesProjection";

const mergeRetryableStatus = (source, projected) => {
  if (
    !source ||
    !projected ||
    !Array.isArray(source.status) ||
    !Array.isArray(projected.status)
  ) {
    throw new Error("Invalid View Images snapshot projection");
  }
  const projectedByTuple = new Map();
  for (const status of projected.status) {
    const tuple = getBulkRowTuples({ ...projected, status: [status] });
    if (tuple.length !== 1 || projectedByTuple.has(tuple[0])) {
      throw new Error("Invalid View Images snapshot projection");
    }
    projectedByTuple.set(tuple[0], status);
  }
  const status = source.status.map((item) => {
    const tuple = getBulkRowTuples({ ...source, status: [item] });
    if (tuple.length !== 1) {
      throw new Error("Invalid View Images snapshot source");
    }
    return projectedByTuple.get(tuple[0]) || item;
  });
  if (
    projectedByTuple.size === 0 ||
    !Array.from(projectedByTuple.keys()).every((tuple) =>
      getBulkRowTuples(source).includes(tuple)
    )
  ) {
    throw new Error("Mismatched View Images snapshot projection");
  }
  return { ...source, status };
};

const createFullRowPersist = ({ rows, orgId, persist }) => {
  const currentRows = new Map();
  for (const row of rows) {
    const key = getBulkRowGuid(row);
    if (
      !key ||
      !Array.isArray(row?.status) ||
      currentRows.has(key) ||
      getViewImagesRowTuples(row, orgId).length !== row.status.length
    ) {
      continue;
    }
    currentRows.set(key, row);
  }
  return async (options) => {
    const key = getBulkRowGuid(options.row);
    const row = mergeRetryableStatus(currentRows.get(key), options.row);
    const persisted = await persist({ ...options, row });
    if (persisted) currentRows.set(key, row);
    return persisted;
  };
};

const knownStatusRequest = (request) => async (options) => {
  const response = await request(options);
  if (
    Array.isArray(response?.data) &&
    response.data.some((item) => !isViewImagesKnownStatus(item?.status))
  ) {
    throw new Error("Unknown View Images snapshot status");
  }
  return response;
};

export const reconcileViewImagesSnapshot = (options = {}) => {
  const hasReviewOrgId =
    options.reviewOrgId !== null && options.reviewOrgId !== undefined;
  const hasExactOrganizationScope =
    options.orgId === options.primaryOrgId
      ? !hasReviewOrgId
      : Number.isSafeInteger(options.reviewOrgId) &&
        options.reviewOrgId === options.orgId;
  if (
    !Number.isSafeInteger(options.orgId) ||
    !Number.isSafeInteger(options.primaryOrgId) ||
    !hasExactOrganizationScope
  ) {
    return Promise.resolve({ outcome: "fallback" });
  }
  const rows = Array.isArray(options.rows) ? options.rows : [];
  const retryableRows = getViewImagesRetryableRows(rows, options.orgId);
  if (retryableRows.length === 0) {
    return Promise.resolve({ outcome: "ignored" });
  }
  const persist = options.persist || persistViewImagesStatusProjection;
  return reconcileBulkUploadSnapshot({
    ...options,
    rows: retryableRows,
    request: knownStatusRequest(options.request || requestBulkUploadSnapshot),
    persist: createFullRowPersist({ rows, orgId: options.orgId, persist }),
  });
};

export const resyncViewImagesStatuses = (options) => (dispatch) =>
  reconcileViewImagesSnapshot({
    ...options,
    publish: (row) => dispatch(updateViewImageListByID(row)),
  });

export default reconcileViewImagesSnapshot;
